import { WSConnection } from './common';
import { EscalationState } from './scan-types';
import { Escalation, UnifiedAlarm, UnifiedAlarmGroup } from './unified-alarm';

export interface UnifiedMonitoringCount {
  highPriority: number;
  lowPriority: number;
}

export enum UnifiedMonitoringMessageType {
  ALARM_COUNT = 'alarm_count',
  UNIFIED_ALARM_GROUPS = 'unified_alarm_groups',
  UNIFIED_ALARMS = 'unified_alarms',
  ALARM_ESCALATION_OPEN = 'alarm_escalation_open',
  ALARM_ESCALATION_CLOSE = 'alarm_escalation_close',
  ALARM_SAFE = 'alarm_safe',
  ORPHAN_ALARM = 'orphan_alarm',
  LLM_RECOMMENDATION = 'llm_recommendation',
  UNIFIED_ALARM_GROUP_STATUS = 'unified_alarm_group_status',
  GET_UNIFIED_ALARM_GROUPS = 'get_unified_alarm_groups',
  ALARM_ALLOCATION = 'alarm_allocation',
}

export enum UnifiedMonitoringProduct {
  SCAN = 'scan',
  REMOTE_GUARDING = 'remote-guarding',
  ENTERPRISE = 'enterprise',
}

export enum UMAllocationQueueState {
  ASSIGNED = 'assigned',
}

export interface UnifiedMonitoringOutboundMessage {
  event_type: UnifiedMonitoringMessageType;
  alarm_group_id?: string;
  product?: UnifiedMonitoringProduct;
  tenant_id?: string;
  existing_unified_alarm_ids?: string[];
  sequence_trace_id?: string;
  alarm_timestamp_sec?: number;
  operator_id?: string;
  vision_tenant_ids?: string[];
  msp_tenant_ids?: string[];
  debug?: boolean;
  camera_id?: string; // backward compatibility with scan messages
  group_id?: string; // backward compatibility with scan messages
  camera_group_id?: string;
  queue_state?: UMAllocationQueueState;
  additional_data?: {
    username?: string;
    comment?: string;
    resolution_reason?: string;
    incident_category?: string;
    situation?: string;
    situation_description?: string;
  };
  pending_high_priority_alarm_count?: number;
  pending_low_priority_alarm_count?: number;
  get_alarms_payload?: GetUnifiedAlarmGroupPayload[];
  // Per-group v3/unification flag (from the group's unificationEnabled). Sent on
  // group-scoped messages so the backend resolves the group through the v3 path.
  // Omitted (undefined) for v2 groups, so legacy frames are byte-identical.
  unification_enabled?: boolean;
  // DB partition (YYYYMM) from the group's partition_key. Sent alongside
  // unification_enabled on group-scoped messages so the backend hits the right
  // partition directly.
  partition_key?: string;
  alarm_type_id?: string; // typed pull (Emergency Alarms)
}

// An alarm type queue (alarmTypeMonitoringQueue); oldest in epoch seconds.
export interface UnifiedMonitoringAlarmTypeQueue {
  id: string;
  name: string;
  count: number;
  oldest_enqueued_at: number | null;
}

export interface UnifiedMonitoringAlarmCountMessage {
  event_type: UnifiedMonitoringMessageType.ALARM_COUNT;
  pending_high_priority_alarm_count: number;
  pending_low_priority_alarm_count: number;
  // Only for operators with alarm type queues; counts are inside the high count.
  alarm_type_queues?: UnifiedMonitoringAlarmTypeQueue[];
}

export interface UnifiedMonitoringAlarmGroupsMessage {
  event_type: UnifiedMonitoringMessageType.UNIFIED_ALARM_GROUPS;
  payload: UnifiedAlarmGroup[];
  count: number;
  comments?: string;
}

export interface UnifiedMonitoringAlarmsMessage {
  event_type: UnifiedMonitoringMessageType.UNIFIED_ALARMS;
  payload: {
    alarms: UnifiedAlarm[];
    alarm_group_id: string;
    product: string;
    escalations: Escalation[];
  };
}

export interface UnifiedMonitoringLLMRecommendationMessage {
  event_type: UnifiedMonitoringMessageType.LLM_RECOMMENDATION;
  payload: {
    alarm_group_id: string;
    headline: string;
    product: string;
    recommendation: string;
    summary: string;
  };
}

export interface UnifiedMonitoringAlarmGroupStatusMessage {
  event_type: UnifiedMonitoringMessageType.UNIFIED_ALARM_GROUP_STATUS;
  payload: {
    alarm_group_id: string;
    status: string;
    product: string;
  };
}

export type UnifiedMonitoringInboundMessage =
  | UnifiedMonitoringAlarmCountMessage
  | UnifiedMonitoringAlarmGroupsMessage
  | UnifiedMonitoringLLMRecommendationMessage;

export type UnifiedMonitoringWSConnection = Omit<WSConnection, 'send'> & {
  send?: (message: UnifiedMonitoringOutboundMessage) => void;
};

export interface CurrentAndSiblingEscalations {
  currentAlarmEscalation?: EscalationState;
  siblingEscalations?: EscalationState[];
}

export interface GetUnifiedAlarmGroupPayload {
  alarm_group_id: string;
  product: UnifiedMonitoringProduct;
  // Set on refresh (from the URL `version=v3` param) so the by-id lookup
  // resolves the group through the v3/unified data path. Without it the
  // backend looks under v2 and returns "not found" for a unified group.
  unification_enabled?: boolean;
  // DB partition (YYYYMM) the WS returned for this group, echoed back so the
  // by-id lookup hits the right partition directly instead of scanning. Sourced
  // on refresh from the URL `pk` param (see UnifiedMonitoringContainer).
  partition_key?: string;
}
